import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { meQueryKey, useMe, type Me } from "@/api/households";
import { useAuth } from "@/auth/AuthProvider";
import {
  DEFAULT_SCHEME,
  DEFAULT_THEME,
  applyScheme,
  applyTheme,
  cachedScheme,
  cachedTheme,
  isScheme,
  isTheme,
  type Scheme,
  type Theme,
} from "./themes";

interface ThemeValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  changing: boolean;
  scheme: Scheme;
  setScheme: (scheme: Scheme) => void;
  changingScheme: boolean;
}

const ThemeContext = createContext<ThemeValue | undefined>(undefined);

/**
 * Decides which theme the interface is drawn in.
 *
 * The choice belongs to the person, like the language (K-36): it is stored on
 * the server against the user and read back from `/me`, so it follows somebody
 * to another device and one member of a household choosing plum does not
 * repaint anybody else's screen. Until `/me` answers, the tab's cached choice
 * (or the default) is used.
 *
 * Changing it is optimistic — the screen repaints on the click, not on the
 * round trip — and `/me` is re-read afterwards so the server's answer wins.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { data: me } = useMe(isAuthenticated);
  const client = useQueryClient();

  const theme: Theme = isTheme(me?.theme) ? me.theme : (cachedTheme() ?? DEFAULT_THEME);
  const scheme: Scheme = isScheme(me?.scheme) ? me.scheme : (cachedScheme() ?? DEFAULT_SCHEME);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    applyScheme(scheme);
  }, [scheme]);

  const change = useMutation({
    mutationFn: (next: Theme) =>
      apiFetch<{ theme: string }>("/api/v1/me/theme", { method: "PUT", body: { theme: next } }),
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: meQueryKey });
      const previous = client.getQueryData<Me>(meQueryKey);
      if (previous) client.setQueryData<Me>(meQueryKey, { ...previous, theme: next });
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context?.previous) client.setQueryData(meQueryKey, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });

  const changeScheme = useMutation({
    mutationFn: (next: Scheme) =>
      apiFetch<{ scheme: string }>("/api/v1/me/scheme", {
        method: "PUT",
        body: { scheme: next },
      }),
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: meQueryKey });
      const previous = client.getQueryData<Me>(meQueryKey);
      if (previous) client.setQueryData<Me>(meQueryKey, { ...previous, scheme: next });
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context?.previous) client.setQueryData(meQueryKey, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });

  const setTheme = useCallback((next: Theme) => change.mutate(next), [change]);
  const setScheme = useCallback((next: Scheme) => changeScheme.mutate(next), [changeScheme]);

  const value = useMemo<ThemeValue>(
    () => ({
      theme,
      setTheme,
      changing: change.isPending,
      scheme,
      setScheme,
      changingScheme: changeScheme.isPending,
    }),
    [theme, setTheme, change.isPending, scheme, setScheme, changeScheme.isPending],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme was called outside ThemeProvider");
  return value;
}
